import { BookOpen, Check, Clock, ExternalLink, Pencil, Plus, Trash2, TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { TaskDTO } from '@tempo/core';
import { api } from '../api';
import { BlackboardForm, TaskForm } from '../components/QuickAdd';
import { Button, Empty, Field, Loading, Modal, NumberInput, Segmented } from '../components/ui';
import { dateTime, minutes, relative } from '../lib/format';
import { useAction, useFeatures, useTasks } from '../lib/hooks';
import { setParams, type Route } from '../lib/router';

const SOURCE_LABEL: Record<string, string> = { 'school-manager': 'School Manager', blackboard: 'Blackboard', api: 'bot', manual: '' };

export function TasksPage({ route }: { route: Route }) {
  const [view, setView] = useState<'active' | 'done'>('active');
  const tasks = useTasks(view === 'done');
  const [editing, setEditing] = useState<TaskDTO | 'new' | null>(null);
  const [logging, setLogging] = useState<TaskDTO | null>(null);
  const [importing, setImporting] = useState(false);
  const { schoolManager } = useFeatures();

  useEffect(() => {
    if (route.params.get('add')) {
      setEditing('new');
      setParams({ add: null });
    }
  }, [route]);
  const openId = route.params.get('open');
  useEffect(() => {
    if (openId && tasks.data) {
      const t = tasks.data.find((x) => x.id === openId);
      if (t) setEditing(t);
      setParams({ open: null });
    }
  }, [openId, tasks.data]);

  const list = (tasks.data ?? []).filter((t) => (view === 'done' ? t.status === 'done' || t.status === 'cancelled' : t.status !== 'done' && t.status !== 'cancelled'));

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="grow">Tasks</h1>
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { value: 'active', label: 'To do' },
            { value: 'done', label: 'Done' },
          ]}
        />
        <Button variant="primary" onClick={() => setEditing('new')}>
          <Plus size={16} /> Add task
        </Button>
      </div>
      <p className="subtle small" style={{ marginBottom: 14 }}>
        Give each task a deadline and how long it'll take – Tempo spreads study sessions over the days before it, around shifts, lectures and your routines.
      </p>
      {tasks.isLoading ? (
        <Loading />
      ) : list.length === 0 ? (
        <div className="card">
          <Empty icon={<BookOpen size={28} />} title={view === 'active' ? 'Nothing to do – nice' : 'Nothing finished yet'}>
            {view === 'active' ? (schoolManager ? 'Add assignments and revision here, or let your uni bot / School Manager send them.' : 'Add assignments and revision here, or paste your Blackboard Due Dates page.') : null}
          </Empty>
          {view === 'active' && !schoolManager && (
            <div className="row" style={{ justifyContent: 'center' }}>
              <Button onClick={() => setImporting(true)}>Add from Blackboard</Button>
            </div>
          )}
        </div>
      ) : (
        <div className="stack">
          {list.map((t) => (
            <TaskCard key={t.id} task={t} onEdit={() => setEditing(t)} onLog={() => setLogging(t)} />
          ))}
        </div>
      )}
      {importing && <BlackboardForm onClose={() => setImporting(false)} />}
      {editing && <TaskForm existing={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} />}
      {logging && <LogTime task={logging} onClose={() => setLogging(null)} />}
    </div>
  );
}

function TaskCard({ task: t, onEdit, onLog }: { task: TaskDTO; onEdit: () => void; onLog: () => void }) {
  const complete = useAction(() => api.patch(`/tasks/${t.id}`, { status: t.status === 'done' ? 'todo' : 'done' }), t.status === 'done' ? 'Reopened' : 'Done 🎉');
  const remove = useAction(() => api.del(`/tasks/${t.id}`), 'Deleted');
  const total = t.estimatedMin || 1;
  const pct = Math.min(100, Math.round((Math.min(t.spentMin, total) / total) * 100));
  const short = t.plan?.shortfallMin ?? 0;
  const external = t.source === 'school-manager';
  const overdue = t.deadline != null && t.deadline < Date.now() && t.status !== 'done';
  const color = t.color ?? '#8b5cf6';

  return (
    <div className="card list-card">
      <div className="accent-bar" style={{ background: color }} />
      <div className="grow">
        <div className="row wrap" style={{ gap: 8 }}>
          <span className="title">{t.title}</span>
          {t.module && <span className="badge accent">{t.module}</span>}
          {SOURCE_LABEL[t.source] !== '' && <span className="badge">{SOURCE_LABEL[t.source] ?? t.source}</span>}
          {t.priority >= 5 && <span className="badge danger">urgent</span>}
        </div>
        <div className="meta">
          {t.deadline ? (
            <span style={{ color: overdue || short > 0 ? 'var(--danger)' : undefined }}>
              Due {dateTime(t.deadline)} · {relative(t.deadline)}
            </span>
          ) : (
            <span>No deadline</span>
          )}
          {t.status !== 'done' && (
            <span>
              {minutes(t.remainingMin)} left{t.plan && t.plan.scheduledMin > 0 ? ` · ${minutes(t.plan.scheduledMin)} scheduled` : ''}
            </span>
          )}
          {t.plan?.finishBy && t.status !== 'done' && short === 0 && <span>· done by {dateTime(t.plan.finishBy)}</span>}
        </div>
        {!external && t.estimatedMin > 0 && (
          <div className="progress" style={{ marginTop: 10, maxWidth: 360 }}>
            <div style={{ width: `${pct}%`, background: color }} />
          </div>
        )}
        {short > 0 && t.status !== 'done' && (
          <div className="small" style={{ color: 'var(--danger)', marginTop: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
            <TriangleAlert size={14} /> {minutes(short)} won't fit before the deadline – free up time or raise the daily study limit.
          </div>
        )}
        {external && <div className="tiny faint" style={{ marginTop: 6 }}>Progress tracked by School Manager</div>}
      </div>
      <div className="actions">
        {t.url && (
          <a className="btn icon sm ghost" href={t.url} target="_blank" rel="noreferrer" title="Open link">
            <ExternalLink size={15} />
          </a>
        )}
        {!external && t.status !== 'done' && (
          <Button size="sm" onClick={onLog} title="Log time">
            <Clock size={15} /> Log
          </Button>
        )}
        <Button size="sm" onClick={() => complete.mutate()} title={t.status === 'done' ? 'Reopen' : 'Mark done'}>
          <Check size={15} /> {t.status === 'done' ? 'Reopen' : 'Done'}
        </Button>
        <Button size="sm" icon variant="ghost" onClick={onEdit} title="Edit">
          <Pencil size={15} />
        </Button>
        <Button size="sm" icon variant="ghost" onClick={() => confirm(`Delete "${t.title}"?`) && remove.mutate()} title="Delete">
          <Trash2 size={15} />
        </Button>
      </div>
    </div>
  );
}

function LogTime({ task, onClose }: { task: TaskDTO; onClose: () => void }) {
  const [mins, setMins] = useState<number | null>(30);
  const log = useAction((m: number) => api.post(`/tasks/${task.id}/log`, { minutes: m }), 'Logged – plan updated');
  return (
    <Modal
      title={`Log time · ${task.title}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!mins} loading={log.isPending} onClick={() => log.mutateAsync(mins!).then(onClose).catch(() => undefined)}>
            Log {mins ? minutes(mins) : ''}
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="chips">
          {[15, 30, 45, 60, 90, 120].map((m) => (
            <button key={m} className={`chip ${mins === m ? 'on' : ''}`} onClick={() => setMins(m)}>
              {minutes(m)}
            </button>
          ))}
        </div>
        <Field label="Minutes" hint="Use a negative number to correct a mistake.">
          <NumberInput value={mins} onChange={setMins} step={5} />
        </Field>
      </div>
    </Modal>
  );
}
